/**
 * Where a "back" control goes.
 *
 * Reported as: the place detail's `← Quay lại` always landed on the places list, however the user had got
 * there. It was a `<Link to="/places">` — a fixed destination wearing the word "back" — so a place opened from
 * the planner, from Home's recent saves or from a pool went to a screen the user had not been on, and the
 * screen they *had* been on was one press away only through the browser's own chrome.
 *
 * The rule is the one a reader assumes: go back to where you came from, and when there is nowhere to go back
 * *to*, go to the screen that makes sense as a parent.
 *
 * "Nowhere to go back to" is the first entry of a history stack — a deep link, a bookmark, or a reload of this
 * page — and react-router marks it with the key `"default"`. The distinction matters because `navigate(-1)`
 * on a stack with nothing behind it inside the app leaves the app entirely, which for a link sent to a friend
 * on Zalo would be a back button that closes the tab. So the fallback is not a nicety here; it is the whole
 * reason the decision is not just "call `navigate(-1)`".
 *
 * The decision is a value rather than a side effect so that it can be tested without a router: the screens
 * call `useGoBack`, which is three lines over this.
 */

/** What a back control should do: step through history, or go straight to a destination. */
export type BackStep = { delta: number } | { to: string };

/** React-router's key for the first entry in the stack: this page was opened, not navigated to. */
const FIRST_ENTRY_KEY = "default";

/**
 * The step a back control takes.
 *
 * `locationKey` is either react-router's `location.key` or `undefined` when there is no router (a test, a
 * render outside a `Router`), which is treated as the first entry for the same reason the marker is: the only
 * safe thing to do without history is the fallback.
 */
export function backStep(locationKey: string | undefined, fallback: string): BackStep {
  if (locationKey === undefined || locationKey === FIRST_ENTRY_KEY) return { to: fallback };
  return { delta: -1 };
}

/** Whether this page was navigated to from somewhere in the app, rather than opened on its own. */
export function cameFromWithinTheApp(locationKey: string | undefined): boolean {
  return locationKey !== undefined && locationKey !== FIRST_ENTRY_KEY;
}
